// for now we only officially support system default OR superhuman
type EmailClient = 'system' | 'superhuman' | 'gmail' | 'spark' | 'airmail' | 'outlook' | 'yahoo';
export const supportedEmailClients = [
  'system',
  'superhuman',
  'gmail',
  'spark',
  'airmail',
  'outlook',
  'yahoo',
];

const supportedEmailClientsSet = new Set(supportedEmailClients);

export const emailClientName: Record<EmailClient, string> = {
  system: 'Default',
  superhuman: 'Superhuman',
  gmail: 'Gmail',
  spark: 'Spark',
  airmail: 'Airmail',
  outlook: 'Microsoft Outlook',
  yahoo: 'Yahoo Mail',
};

const urlScheme: Record<EmailClient, string> = {
  system: 'mailto',
  superhuman: 'superhuman',
  gmail: 'googlegmail',
  spark: 'readdle-spark',
  airmail: 'airmail',
  outlook: 'ms-outlook',
  yahoo: 'ymail',
};

const urlRoot: Record<EmailClient, string> = {
  system: '',
  superhuman: '//compose',
  gmail: '///co',
  spark: '//compose',
  airmail: '//compose',
  outlook: '//compose',
  yahoo: '//mail/compose',
};

const recipientKey: Record<EmailClient, string> = {
  system: 'to',
  superhuman: 'to',
  gmail: 'to',
  spark: 'recipient',
  airmail: 'to',
  outlook: 'to',
  yahoo: 'to',
};

const subjectKey: Record<EmailClient, string> = {
  system: 'subject',
  superhuman: 'subject',
  gmail: 'subject',
  spark: 'subject',
  airmail: 'subject',
  outlook: 'subject',
  yahoo: 'subject',
};

const bodyKey: Record<EmailClient, string> = {
  system: 'body',
  superhuman: 'body',
  gmail: 'body',
  spark: 'body',
  airmail: 'plainBody',
  outlook: 'body',
  yahoo: 'body',
};

const storageKey = 'compose_email_client';

// The first time we try to read the user's preferred email client, we check the stored
// value for their preferred Moments email client (either empty or 'superhuman'). We write
// this value ('system' if empty, otherwise 'superhuman') so that we don't try to perform
// this check again.
export const backfillExistingEmailClientPreference = () => {
  if (window.localStorage.getItem(storageKey)) {
    return;
  }

  const momentsEmailClient = window.localStorage.getItem('email_client') || 'system';
  setPreferredEmailClient(momentsEmailClient as EmailClient);
};

export const getPreferredEmailClient = (): EmailClient => {
  const preferredEmailClient = window.localStorage.getItem(storageKey) as EmailClient;
  return supportedEmailClientsSet.has(preferredEmailClient) ? preferredEmailClient : 'system';
};

export const setPreferredEmailClient = (emailClient: EmailClient) => {
  window.localStorage.setItem(storageKey, emailClient);
};

export const supportHref = ({ subject, body }: { subject?: string; body?: string } = {}) =>
  emailURL({ recipient: 'care@clay.earth', subject: subject || 'Support', body });

export default function emailURL({
  recipient,
  subject,
  body,
}: {
  recipient?: string;
  subject?: string;
  body?: string;
}): string {
  const client = getPreferredEmailClient();
  let url = `${urlScheme[client]}:${urlRoot[client]}`;
  const queryParams: [string, string][] = [];

  if (recipient) {
    if (client === 'system') {
      url = url + recipient;
    } else {
      queryParams.push([recipientKey[client], recipient]);
    }
  }

  if (subject) {
    queryParams.push([subjectKey[client], subject]);
  }

  if (body) {
    queryParams.push([bodyKey[client], body]);
  }

  const queryParamsString = queryParams.map(([paramKey, val]) => `${paramKey}=${val}`).join('&');

  if (queryParamsString.length > 0) {
    url = url + '?' + queryParamsString;
  }

  return url;
}
